import { useEffect, useState } from "react";
import { api } from "@/lib/config";
import { useContent, C } from "@/hooks/useContent";
import { useSections, visible } from "@/hooks/useSections";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MapPin, Maximize2, ArrowRight } from "lucide-react";
import BeforeAfter from "@/components/BeforeAfter";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const SPAN_CLASSES = {
  normal: "md:col-span-6",
  wide: "md:col-span-8",
  tall: "md:col-span-4 md:row-span-2",
};

export default function PortfolioPage() {
  const content = useContent();
  const sections = useSections();
  const [items, setItems] = useState([]);
  useDocumentMeta({
    title: "Portfolio · Before & After · Northwestern Exterior Cleaning",
    description: "See before-and-after photos of recent pressure washing and exterior cleaning projects across North Seattle — concrete, brick, pavers, wood and composite.",
    path: "/portfolio",
  });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portfolio");
        setItems(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="portfolio-page" className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-16">
      <div className="max-w-3xl">
        <p className="overline text-muted-foreground">{C(content, "portfolio.eyebrow", "Past work")}</p>
        <h1 className="font-display text-5xl lg:text-6xl font-semibold tracking-tighter mt-2 leading-[1.02]">
          {C(content, "portfolio.heading", "The proof is in the pudding.")}
        </h1>
        <p className="text-muted-foreground mt-4 text-lg max-w-xl leading-relaxed">
          {C(content, "portfolio.body", "Real jobs across North Seattle. Drag the slider on each card to see before and after.")}
        </p>
      </div>

      {visible(sections, "portfolio.items") && (loading ? (
        <div className="mt-10 grid md:grid-cols-12 gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="md:col-span-6 aspect-[4/3] bg-muted rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="mt-10 grid md:grid-cols-12 gap-5 auto-rows-auto" data-testid="portfolio-grid">
          {items.map((it, idx) => (
            <Card
              key={it.id}
              data-testid={`portfolio-item-${idx}`}
              className={`overflow-hidden p-0 border-border hover-lift ${SPAN_CLASSES[it.span] || SPAN_CLASSES.normal}`}
            >
              <div className="relative">
                <BeforeAfter before={it.before_image} after={it.after_image} alt={it.title} />
                <button
                  onClick={() => setActive(it)}
                  data-testid={`portfolio-expand-${idx}`}
                  className="absolute bottom-3 right-3 bg-white/90 hover:bg-white p-2 rounded-full shadow"
                  aria-label="expand"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3" />
                  <span>{it.location}</span>
                  <span>·</span>
                  <span>{it.sqft.toLocaleString()} sqft</span>
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight mt-2">{it.title}</h3>
                <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">{it.description}</p>
              </div>
            </Card>
          ))}
        </div>
      ))}

      {visible(sections, "portfolio.cta") && (
      <div className="mt-14 bg-primary text-primary-foreground rounded-3xl p-10 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-testid="portfolio-cta-section">
        <div className="max-w-xl">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight">
            {C(content, "portfolio.cta_heading", "Want your driveway in this gallery next month?")}
          </h2>
          <p className="text-white/75 mt-4">{C(content, "portfolio.cta_body", "Pick a slot. We'll send you the before/after when we're done.")}</p>
        </div>
        <Link to="/booking">
          <Button data-testid="portfolio-cta-book" size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full h-12 px-7">
            {C(content, "portfolio.cta_button", "Book your job")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
      )}

      {/* Lightbox */}
      {active && (
        <div
          onClick={() => setActive(null)}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm grid place-items-center p-6"
          data-testid="portfolio-lightbox"
        >
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <BeforeAfter before={active.before_image} after={active.after_image} alt={active.title} />
            <div className="mt-4 text-white">
              <p className="font-display text-2xl font-semibold">{active.title}</p>
              <p className="text-white/70 text-sm mt-1">{active.location} · {active.sqft.toLocaleString()} sqft</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
